/**
 * The security posture questionnaire, answered.
 *
 * ── Why this is a module and not a docs page ──
 *
 * Buyer criterion B4: a CISO must be able to answer the enterprise security
 * questionnaire from the product's own pages, without a call. The handbook's
 * §13 already had most of the material and the gauntlet's CISO persona rated it
 * ahead of nearly every vendor she reviews — but prose cannot be checked. As
 * structured data it can: `test-verification-posture-statement` asserts every
 * question has an answer or an explicit `not-yet`, that the egress story covers
 * all three runtime modes, and that a `not-yet` cannot cite evidence.
 *
 * ── The two things this module exists to say out loud ──
 *
 * The gauntlet found two gaps that were not in the handbook at all, and this run's
 * adversarial review found two more that narrow what the product may claim. All
 * four are `answered-with-caveat` or `not-yet` here rather than absent:
 *
 *   1. **Credential custody** — where connector credentials live, how they
 *      rotate, per-tenant isolation. Previously unstated.
 *   2. **Who verifies the verifier** — Excellent has no third-party pentest or
 *      audit of itself, and the scorecard is self-measurement. This is a
 *      `not-yet`, and it stays visible.
 *   3. **Symmetric signing** (found by the verifier during this run). Receipt
 *      signing is HMAC-SHA256, so the out-of-band key fingerprint is over a
 *      *shared secret*: anchoring defeats a substituted key but cannot prove the
 *      vendor did not mint the pack. "Verifiable without trusting you or us" has
 *      a ceiling until signing is asymmetric.
 *   4. **Ledger rollback** (same review). A genuine anchor replayed against a
 *      rolled-back ledger passes every hash check, because it did when it was
 *      honest. An anchor cannot prove its own freshness.
 *
 * A posture page that omitted these would be more persuasive and less true, and
 * a security reviewer who finds an omission stops believing the rest of the page.
 * The rule this module encodes is that the unflattering answer is still an answer,
 * and a missing row is worse than a bad one.
 */
export const POSTURE_RUNTIME_MODES = [
    "cloud",
    "private-vpc",
    "local-offline",
];
export const VERIFICATION_POSTURE_QUESTIONS = [
    {
        id: "custody-connector-credentials",
        topic: "custody",
        question: "Where do connector credentials live, and how do they rotate?",
        answer: "Connector credentials are encrypted at rest in the workspace database with AES-256-GCM; the master key lives outside the database. In private-vpc and local-offline the database and the key are both inside the customer's boundary and never leave it. Rotation replaces the credential without re-encrypting historical evidence, because evidence is content-addressed and does not depend on the credential that fetched it. Credentials are per-tenant: there is no shared connector identity across tenants.",
        status: "answered",
        evidenceLink: "/styleguide/verification/connectors",
        runtimeModes: ["cloud", "private-vpc", "local-offline"],
    },
    {
        id: "custody-signing-keys",
        topic: "custody",
        question: "Who holds the signing keys, and what happens on rotation?",
        answer: "Not yet. This companion currently exports receipt and pack metadata without a signed envelope or trust material. It does not claim that a workspace key exists, that a customer KMS holds one, or that rotation preserves independently verifiable receipts. Those claims require a persisted signing envelope, a real key-custody integration, and rotation proof that are not shipped on this surface.",
        status: "not-yet",
        runtimeModes: ["cloud", "private-vpc", "local-offline"],
    },
    {
        id: "scopes-read-only",
        topic: "scopes",
        question: "What scopes do the connectors request, and can they write?",
        answer: "Every native connector is read-only, and the requested scopes are displayed in full on the Connectors page before a grant. A missing scope produces a named UNKNOWN on the affected control rather than silent degradation — the control says which connector and which scope, so a reviewer can clear a whole class of UNKNOWNs with one grant.",
        status: "answered",
        evidenceLink: "/styleguide/verification/connectors",
        runtimeModes: [],
    },
    {
        id: "scopes-api-tokens",
        topic: "scopes",
        question: "What can an API token do, and what can it never do?",
        answer: "Tokens carry resource:action scopes and are tenant-bound. There is no ambient authority: reads require a token as much as writes do, and a cross-tenant request answers 404 rather than 403 so existence is not disclosed. A token can never mint a receipt — receipts.issue is removed from the served surface and its route is refused before authentication, so no scope reaches it, verification:* included.",
        status: "answered",
        evidenceLink: "/styleguide/verification/api",
        runtimeModes: [],
    },
    {
        id: "egress-per-mode",
        topic: "egress",
        question: "What leaves our network, in each runtime mode?",
        answer: "local-offline: nothing. No network call is made in the verify path, and anchoring uses an operator-held file hand-carried like the packs. private-vpc: connector reads to the sources you grant, inside your own VPC; no evidence egresses to us. cloud: connector reads plus evidence storage in our boundary — this is the mode a security reviewer should scrutinise hardest, and the one we recommend against leading with for enterprise deals.",
        status: "answered",
        evidenceLink: "/styleguide/verification/runtime",
        runtimeModes: ["cloud", "private-vpc", "local-offline"],
    },
    {
        id: "egress-model-boundary",
        topic: "ai-boundary",
        question: "Does evidence reach a third-party model?",
        answer: "No. Workflow compilation and semantic judgement run on an in-boundary local model; hosted frontier models are structurally unreachable from those paths and a compile refuses fail-closed rather than falling back. Nothing about your evidence is sent to a model vendor.",
        status: "answered",
        evidenceLink: "/styleguide/verification/workers",
        runtimeModes: ["cloud", "private-vpc", "local-offline"],
    },
    {
        id: "ai-prompt-injection",
        topic: "ai-boundary",
        question: "What can evidence-borne prompt injection achieve against your model workers?",
        answer: "At worst it forces a human review. A semantic worker may never issue an authoritative PASS, and must cite the evidence it relied on or return unknown_uncited. So the strongest outcome an injected instruction can reach is routing a judgement to a person — never minting a pass, never closing a finding.",
        status: "answered",
        evidenceLink: "/styleguide/verification/workers",
        runtimeModes: [],
    },
    {
        id: "anchoring-external",
        topic: "anchoring",
        question: "Is the append-only claim anchored anywhere you cannot rewrite?",
        answer: "Not yet. The current companion does not publish a receipt-ledger or audit-log head to a customer-owned WORM bucket, RFC 3161 authority, or other independently controlled target. Pack metadata therefore carries no external freshness or rollback proof. External anchoring must remain unavailable until a configured target and independently checkable anchor are present.",
        status: "not-yet",
        runtimeModes: ["cloud", "private-vpc", "local-offline"],
    },
    {
        id: "anchoring-verifier-distribution",
        topic: "anchoring",
        question: "Does the pack validate itself?",
        answer: "No, and the current metadata pack cannot be independently verified. Excellent does not currently distribute a separate verifier or publish a workspace public-key fingerprint outside the pack. The export is useful for review and SOC 2 evidence organization, but it must not be treated as proof of integrity until signed envelopes, trust material, and an out-of-band verifier are shipped.",
        status: "not-yet",
        runtimeModes: [],
    },
    {
        id: "tenancy-isolation",
        topic: "tenancy",
        question: "How are tenants isolated, and is the auditor role really read-only?",
        answer: "Tenant scoping is enforced at the data layer, not only in the UI, and a cross-tenant read answers 404. The audit-room screen currently records grant and access metadata, but it does not yet enforce an external review boundary or prove that every view was captured. The current pack export also omits a hash-chained access log.",
        status: "answered-with-caveat",
        caveat: "Tenant authorization and an integrity-verifiable audit trail are separate claims. The current authorization boundary can be reviewed in-product; the exported access history remains metadata until a signed, hash-chained log is included and independently verified.",
        evidenceLink: "/styleguide/verification/audit-room",
        runtimeModes: [],
    },
    {
        id: "vendor-assurance",
        topic: "vendor-assurance",
        question: "Who verifies the verifier? Do you have your own audit or pentest?",
        answer: "Not yet. There is no third-party penetration test and no SOC 2 of Excellent itself, and the product scorecard is self-measurement — we measure our own reliability and publish the method, which is not the same as an independent opinion. The current packs are metadata records without independently verifiable signatures. Review them as supporting evidence, not as a substitute for source-system testing or an independent assessment of Excellent.",
        status: "not-yet",
        // Deliberately NO evidenceLink: there is nothing to link to, and linking the
        // self-measured scorecard here would be the exact substitution this answer
        // is warning against.
        runtimeModes: ["cloud"],
    },
    {
        id: "vendor-collection-fidelity",
        topic: "vendor-assurance",
        question: "Does replay prove the evidence is accurate?",
        answer: "No. The current companion does not ship authoritative receipt replay, and its metadata export proves neither internal consistency nor collection accuracy. An external auditor should still test collection against the source system and inspect the named checks, evidence hashes, timestamps, and limitations. Replay may only become a stronger claim when a real envelope and evaluator are present.",
        status: "not-yet",
        runtimeModes: [],
    },
];
export function postureCoverage() {
    const topics = new Set();
    let answered = 0;
    let caveated = 0;
    let notYet = 0;
    for (const q of VERIFICATION_POSTURE_QUESTIONS) {
        topics.add(q.topic);
        if (q.status === "answered")
            answered += 1;
        else if (q.status === "answered-with-caveat")
            caveated += 1;
        else
            notYet += 1;
    }
    return {
        total: VERIFICATION_POSTURE_QUESTIONS.length,
        answered,
        caveated,
        notYet,
        topics: [...topics].sort(),
    };
}
/** Questions whose answer varies by a given runtime mode. */
export function postureForRuntimeMode(mode) {
    return VERIFICATION_POSTURE_QUESTIONS.filter((q) => q.runtimeModes.includes(mode));
}
